import React, { FC, useMemo, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { Button } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { ScreenQuestionProps } from '../JobPostX/types';
import { SelectableTagList } from '../JobPostX/PreDefinedTags';
import {
  CurrentSalaryCard,
  ExpectedSalaryCard,
  LanguageCard,
  NoticePeriodCard,
  EducationCard,
  SkillCard,
  WorkExperienceCard,
  YesNoCard,
  WorkModeCard,
} from '../JobPostX/ScreenQuestionCard';
import { CustomQuestionCard } from '../JobPostX/CustomQuestionCard';
import { createNoticePeriodQuestion } from '../JobPostX/noticePeriodConfig';
import {
  createExpectedSalaryQuestion,
  answerFromSalaryRange,
} from '../JobPostX/expectedSalaryConfig';
import { createWorkExperienceQuestion } from '../JobPostX/workExperienceConfig';
import {
  createSkillExperienceQuestion,
  getSkillExperienceQuestionText,
} from '../JobPostX/skillExperienceConfig';
import { createEducationQuestion, EDUCATION_QUESTION_TEXT } from '../JobPostX/educationConfig';
import {
  createLanguageQuestion,
  LANGUAGE_QUESTION_TEXT,
  LANGUAGE_PROFICIENCY_VALUE_MAP,
} from '../JobPostX/languageConfig';
const titleKey = (t: ScreenQuestionProps) =>
  t.title?.toString().trim().toLowerCase().replace(/\s+/g, '-');

type Props = {
  value?: ScreenQuestionProps[];
  onChange?: (value?: ScreenQuestionProps[]) => void;
  placeholder?: string;
  predefinedQuestions: ScreenQuestionProps[];
};

export const ScreenQuestions: FC<Props> = (props) => {
  const { value, onChange, predefinedQuestions } = props;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const questions: any = value ?? [];

  useEffect(() => {
    if (!Array.isArray(value) || value.length === 0) return;
    let mutated = false;

    const normalized = value.map((q) => {
      if (!q) return q;
      const next = { ...q } as any;
      let changed = false;

      if (!next.id) {
        next.id = uuid();
        changed = true;
      }

      if (next.type === 'SalaryRange') {
        const legacySalary = next.salary;
        const expectedAnswer = next.expectedAnswer;
        const expectedLooksLikeSalary =
          expectedAnswer &&
          typeof expectedAnswer === 'object' &&
          (Object.prototype.hasOwnProperty.call(expectedAnswer, 'amount') ||
            Object.prototype.hasOwnProperty.call(expectedAnswer, 'fromAmount') ||
            Object.prototype.hasOwnProperty.call(expectedAnswer, 'salary') ||
            Object.prototype.hasOwnProperty.call(expectedAnswer, 'fromSalary') ||
            Object.prototype.hasOwnProperty.call(expectedAnswer, 'type'));

        if (legacySalary !== undefined) {
          delete next.salary;
          changed = true;
        }

        if (expectedLooksLikeSalary) {
          const derived = answerFromSalaryRange(expectedAnswer as any);
          if (derived) next.expectedAnswer = derived;
          else delete next.expectedAnswer;
          changed = true;
        } else if (!next.expectedAnswer && legacySalary) {
          const derived = answerFromSalaryRange(legacySalary);
          if (derived) {
            next.expectedAnswer = derived;
            changed = true;
          }
        }
      } else if (next.type === 'Salary') {
        const legacySalary = next.salary;
        const legacySalaryType =
          typeof next.salaryType === 'string' && next.salaryType.trim().length > 0
            ? next.salaryType.trim()
            : legacySalary &&
              typeof legacySalary === 'object' &&
              typeof legacySalary.type === 'string'
            ? legacySalary.type
            : undefined;

        if (legacySalary !== undefined) {
          delete next.salary;
          changed = true;
        }
        if (next.salaryType !== undefined) {
          delete next.salaryType;
          changed = true;
        }
        if (next.currency !== undefined) {
          delete next.currency;
          changed = true;
        }

        if (next.expectedAnswer && typeof next.expectedAnswer !== 'string') {
          delete next.expectedAnswer;
          changed = true;
        }

        if (!next.expectedAnswer) {
          next.expectedAnswer = legacySalaryType ?? 'Month';
          changed = true;
        }
      } else if (next.type === 'WorkExperience') {
        const legacyYears =
          typeof next.yearsRequired === 'string' && next.yearsRequired.trim().length > 0
            ? next.yearsRequired.trim()
            : undefined;
        const expectedYears =
          typeof next.expectedAnswer === 'string' && next.expectedAnswer.trim().length > 0
            ? next.expectedAnswer.trim()
            : undefined;

        if (legacyYears && legacyYears !== expectedYears) {
          next.expectedAnswer = legacyYears;
          changed = true;
        }

        if (next.yearsRequired !== undefined) {
          delete next.yearsRequired;
          changed = true;
        }

        if (next.answerType !== 'numeric') {
          next.answerType = 'numeric';
          changed = true;
        }
      } else if (next.type === 'Skills') {
        const legacyYears =
          typeof next.yearsRequired === 'string' && next.yearsRequired.trim().length > 0
            ? next.yearsRequired.trim()
            : undefined;
        const expectedYears =
          typeof next.expectedAnswer === 'string' && next.expectedAnswer.trim().length > 0
            ? next.expectedAnswer.trim()
            : undefined;

        if (legacyYears !== undefined && legacyYears !== expectedYears) {
          next.expectedAnswer = legacyYears;
          changed = true;
        } else if (expectedYears !== undefined && expectedYears !== next.expectedAnswer) {
          next.expectedAnswer = expectedYears;
          changed = true;
        }

        if (next.yearsRequired !== undefined) {
          delete next.yearsRequired;
          changed = true;
        }

        if (next.answerType !== 'numeric') {
          next.answerType = 'numeric';
          changed = true;
        }

        const desiredQuestion = getSkillExperienceQuestionText(next.jobFunction);
        if (next.question !== desiredQuestion) {
          next.question = desiredQuestion;
          changed = true;
        }
      } else if (next.type === 'Education') {
        const level = typeof next.educationLevel === 'string' ? next.educationLevel : '';
        if (next.educationLevel !== level) {
          next.educationLevel = level;
          changed = true;
        }

        const yesNo = typeof next.expectedYesNo === 'string' ? next.expectedYesNo : '';
        const answer =
          typeof next.expectedAnswer === 'string' && next.expectedAnswer.length > 0
            ? next.expectedAnswer
            : yesNo;

        if (answer !== next.expectedAnswer) {
          next.expectedAnswer = answer;
          changed = true;
        }

        if (answer !== yesNo) {
          next.expectedYesNo = answer;
          changed = true;
        }

        if (next.question !== EDUCATION_QUESTION_TEXT) {
          next.question = EDUCATION_QUESTION_TEXT;
          changed = true;
        }

        if (next.answerType !== 'YesNo') {
          next.answerType = 'YesNo';
          changed = true;
        }
      } else if (next.type === 'Language') {
        const lang = typeof next.language === 'string' ? next.language.trim() : '';
        if (next.language !== lang) {
          next.language = lang;
          changed = true;
        }

        if (next.question !== LANGUAGE_QUESTION_TEXT) {
          next.question = LANGUAGE_QUESTION_TEXT;
          changed = true;
        }

        const proficiency = typeof next.proficiency === 'string' ? next.proficiency : '';
        const expected =
          typeof next.expectedAnswer === 'string' && next.expectedAnswer.length > 0
            ? next.expectedAnswer
            : proficiency;

        if (expected !== next.expectedAnswer) {
          next.expectedAnswer = expected;
          changed = true;
        }

        if (expected !== proficiency) {
          next.proficiency = expected;
          changed = true;
        }

        const desiredWeight = expected ? LANGUAGE_PROFICIENCY_VALUE_MAP[expected] : undefined;
        if (next.idealWeight !== desiredWeight) {
          next.idealWeight = desiredWeight;
          changed = true;
        }

        if (next.answerType !== 'SingleChoice') {
          next.answerType = 'SingleChoice';
          changed = true;
        }
      } else if (next.type === 'YesNo') {
        const yesNo = typeof next.expectedYesNo === 'string' ? next.expectedYesNo : '';
        const expected =
          typeof next.expectedAnswer === 'string' && next.expectedAnswer.length > 0
            ? next.expectedAnswer
            : yesNo;

        if (expected !== next.expectedAnswer) {
          next.expectedAnswer = expected;
          changed = true;
        }

        if (expected !== yesNo) {
          next.expectedYesNo = expected;
          changed = true;
        }

        if (next.answerType !== 'YesNo') {
          next.answerType = 'YesNo';
          changed = true;
        }
      }

      if (!changed) return q;
      mutated = true;
      return next;
    });

    if (mutated) {
      onChange?.(normalized);
    }
  }, [value, onChange]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const additiveTypes = new Set(['WorkExperience', 'Language', 'Skills']);

  const selectedKeys = useMemo(() => {
    return questions.filter((q) => !additiveTypes.has(q.type as any)).map((q) => titleKey(q));
  }, [additiveTypes, questions]);

  const activeKeys = useMemo(() => {
    const keys: string[] = [];
    const we = predefinedQuestions.find((q) => q.type === 'WorkExperience');
    const lang = predefinedQuestions.find((q) => q.type === 'Language');
    const skills = predefinedQuestions.find((q) => q.type === 'Skills');
    if (questions.some((q) => q.type === 'WorkExperience') && we) keys.push(titleKey(we));
    if (questions.some((q) => q.type === 'Language') && lang) keys.push(titleKey(lang));
    if (questions.some((q) => q.type === 'Skills') && skills) keys.push(titleKey(skills));
    return keys;
  }, [questions, predefinedQuestions]);

  const forcePlusKeys = activeKeys;

  const emit = (next: ScreenQuestionProps[]) => onChange?.(next);

  const initByType = (tag: ScreenQuestionProps): ScreenQuestionProps => {
    switch (tag.type) {
      case 'WorkExperience':
        return createWorkExperienceQuestion({
          title: tag.title,
          question: tag.question,
          category: 'PreDefined',
          required: false,
        });

      case 'Language':
        return createLanguageQuestion({
          title: tag.title,
          question: tag.question,
          category: 'PreDefined',
          required: false,
        });

      case 'Skills':
        return createSkillExperienceQuestion({
          title: tag.title,
          question: tag.question,
          category: 'PreDefined',
          required: false,
        });

      case 'Select':
        return {
          type: 'Select',
          title: tag.title,
          category: 'PreDefined',
          question: tag.question,
          required: false,
          answerType: 'SingleChoice',
          options: tag.options ?? [],
          expectedAnswer: '',
        } as any;

      case 'YesNo':
        return {
          type: 'YesNo',
          title: tag.title,
          category: 'PreDefined',
          question: tag.question,
          required: false,
          answerType: 'YesNo',
          expectedYesNo: '',
          expectedAnswer: '',
        } as any;

      case 'SalaryRange':
        return createExpectedSalaryQuestion({
          title: tag.title,
          question: tag.question,
          category: 'PreDefined',
          required: false,
        });

      case 'Salary':
        return {
          type: 'Salary',
          title: tag.title,
          category: 'PreDefined',
          question: tag.question,
          required: false,
          answerType: 'numeric',
          expectedAnswer: 'Month',
        } as any;

      case 'Education':
        return createEducationQuestion({
          title: tag.title,
          category: 'PreDefined',
          required: false,
        });

      case 'NoticePeriod':
        return createNoticePeriodQuestion({
          title: tag.title,
          question: tag.question,
          category: 'PreDefined',
          required: false,
        });

      default:
        return {
          type: tag.type,
          title: tag.title,
          category: 'PreDefined',
          question: tag.question,
          required: false,
          expectedYesNo: '',
        } as any;
    }
  };

  const addQuestion = (tag: ScreenQuestionProps) => {
    const next = questions.slice();
    if (!additiveTypes.has(tag.type as any)) {
      const exists =
        tag.type === 'YesNo'
          ? next.find((q) => titleKey(q) === titleKey(tag))
          : next.find((q) => q.type === tag.type);
      if (exists) return;
    }
    const initialized = initByType(tag);
    if (!initialized.id) {
      initialized.id = uuid();
    }
    next.push(initialized);
    emit(next);
  };

  const removeByTag = (tag: ScreenQuestionProps) => {
    const key = titleKey(tag);
    const idx = questions.findIndex((q) => titleKey(q) === key || q.type === tag.type);
    if (idx >= 0) {
      const next = questions.slice();
      next.splice(idx, 1);
      emit(next);
    }
  };

  const updateAt = (index: number, patch: any) => {
    const next = questions.slice();
    const current = { ...(next[index] as any), ...patch } as any;
    if (current?.type === 'NoticePeriod') {
      delete current.noticePeriod;
    }
    if (current?.type === 'SalaryRange') {
      delete current.salary;
    }
    if (current?.type === 'Salary') {
      delete current.salary;
      delete current.salaryType;
      delete current.currency;
    }
    if (current?.type === 'WorkExperience') {
      delete current.yearsRequired;
    }
    if (current?.type === 'Skills') {
      delete current.yearsRequired;
    }
    next[index] = current;
    emit(next);
  };

  const removeAt = (index: number) => {
    const next = questions.slice();
    next.splice(index, 1);
    emit(next);
  };

  const onTagChange = (_nextSelectedKeys: string[], { tag, selected }: any) => {
    if (additiveTypes.has(tag.type as any)) {
      addQuestion(tag);
      return;
    }
    if (selected) addQuestion(tag);
    else removeByTag(tag);
  };

  // 👉 CUSTOM QUESTION SUPPORT (use category to identify)
  const addCustomQuestion = () => {
    const custom: ScreenQuestionProps = {
      id: uuid(),
      type: 'Text',
      title: '',
      category: 'Custom',
      required: false,
      question: '',
    };
    emit([...questions, custom]);
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <SelectableTagList
          items={predefinedQuestions}
          selectedKeys={selectedKeys}
          size="small"
          activeKeys={activeKeys}
          forcePlusKeys={forcePlusKeys}
          onChange={onTagChange}
        />
      </div>

      {/* Render all questions */}
      <div className="flex flex-col mt-5 mb-2 gap-4">
        {questions.map((q, index) => {
          // 👉 CUSTOM QUESTION RENDERING BASED ON CATEGORY
          if (q.category === 'Custom') {
            return (
              <CustomQuestionCard
                key={index}
                value={q}
                onChange={(v) => updateAt(index, v)}
                onRemove={() => removeAt(index)}
              />
            );
          }

          // Predefined question types
          switch (q.type) {
            case 'WorkExperience':
              return (
                <WorkExperienceCard
                  key={index}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  deletable
                  onDelete={() => removeAt(index)}
                />
              );

            case 'Language':
              return (
                <LanguageCard
                  key={index}
                  title={q.title}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  deletable
                  onDelete={() => removeAt(index)}
                />
              );

            case 'Skills':
              return (
                <SkillCard
                  key={index}
                  title={q.title}
                  question={q.question}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  onDelete={() => removeAt(index)}
                />
              );

            case 'Education':
              return (
                <EducationCard
                  key={index}
                  title={q.title}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  deletable
                  onDelete={() => removeAt(index)}
                />
              );

            case 'SalaryRange':
              return (
                <ExpectedSalaryCard
                  key={index}
                  title={q.title}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  deletable
                  onDelete={() => removeAt(index)}
                />
              );

            case 'Salary':
              return (
                <CurrentSalaryCard
                  key={index}
                  title={q.title}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  deletable
                  onDelete={() => removeAt(index)}
                />
              );

            case 'NoticePeriod':
              return (
                <NoticePeriodCard
                  key={index}
                  title={q.title}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  deletable
                  onDelete={() => removeAt(index)}
                />
              );

            case 'Select':
              return (
                <WorkModeCard
                  key={index}
                  title={q.title}
                  question={q.question}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  onDelete={() => removeAt(index)}
                />
              );

            default:
              return (
                <YesNoCard
                  key={index}
                  title={q.title}
                  question={q.question}
                  value={q}
                  onChange={(v) => updateAt(index, v)}
                  onDelete={() => removeAt(index)}
                />
              );
          }
        })}
      </div>

      {/* Add new question button */}
      <div>
        <Button
          type="text"
          onClick={addCustomQuestion}
          className={[
            'w-full flex items-center justify-center gap-2',
            'py-3 px-4',
            'rounded-md border border-gray-200 bg-gray text-gray-700',
            '!hover:bg-[#18191C] hover:text-gray-800',
            '!active:bg-[#18191C]',
            'dark:bg-gray dark:text-[#18191C]',
            '!dark:hover:bg-[#18191C] dark:hover:text-white',
          ].join(' ')}
          style={{ height: 'auto' }}>
          <PlusCircleOutlined />
          <span className="text-sm font-medium">Add New Questions</span>
        </Button>
      </div>
    </div>
  );
};
